import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from '../../shared/services/login.service';
import { BrandService } from '../../shared/services/brand.service';
import { AuthStateService } from '../../shared/services/auth-state.service';
import { NotificationService, NotificationType } from '../../shared/services/notification.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private login: LoginService,
    private router: Router,
    private brandService: BrandService,
    private authState: AuthStateService,
    private notificationService: NotificationService,
    private translate: TranslateService,
  ) {}

  canActivate(state: RouterStateSnapshot): boolean {
    if (!this.login.isTokenValid()) {
      this.router.navigate(['/login'], {
        queryParams: { redirectUrl: state.url },
      });
      return false;
    }

    // Guard against stale/incomplete client state after refresh (e.g. SSO marker exists
    // but productType/roles were not restored), which previously caused access-denied loops.
    if (!this.authState.productType) {
      this.authState.clear();
      this.router.navigate(['/login'], {
        queryParams: { redirectUrl: state.url },
      });
      return false;
    }

    const { ok, correctUrl } = this.brandService.validateUserBrand(this.authState.productType, this.authState.roles);

    if (!ok) {
      // Only redirect to configured brand publicUrls on the SAME environment tier (test→test,
      // prod→prod). isKnownBrandUrl enforces the tier check; a cross-tier `correctUrl` (e.g.,
      // a stale prod URL when running on test) is treated as unsafe and falls through to
      // /access-denied with a user notification.
      const isSafeUrl = correctUrl && this.brandService.isKnownBrandUrl(correctUrl);
      if (isSafeUrl) {
        // Full page reload is intentional: navigating to a different brand subdomain requires
        // Angular to re-bootstrap with the correct runtime-config.js and brand context.
        // Angular Router cannot navigate cross-origin, and even same-origin cross-brand
        // navigation needs a fresh app bootstrap to pick up the new brand's theme and config.
        this.navigateFullPage(correctUrl!);
      } else {
        // /access-denied is not behind AuthGuard, so redirecting there cannot cause a loop.
        this.notificationService.showNotification(NotificationType.Error, this.translate.instant('ERROR_MESSAGES.BRAND_MISMATCH'));
        this.router.navigate(['/access-denied'], { queryParams: { from: state.url } });
      }
      return false;
    }

    return true;
  }

  navigateFullPage(url: string): void {
    window.location.href = url;
  }
}
